// filepath: apps/web/src/admin/formula/FormulaEditor.tsx
//
// FR-UI-ADM-3 — single, reusable formula-editor component.
//
// The widget is the SPA-side mirror of FR-VAL-4 (server-side
// expr-lang/expr compile/type-check). It is composed into the THREE
// consumer surfaces without per-surface fork:
//   1. Form-field visibility rules (`visibleWhen` slot in
//      `FieldMetadata`).
//   2. Form-field computed-value rules (`computedFrom` slot in
//      `FieldMetadata`).
//   3. BPM process-node alert-expression rules (deferred [S]
//      surface per ARCHITECTURE_WEB.md §15).
//
// INVARIANTS (binding; gate failures = BLOCKER):
//   - INV-2 (visibility ≠ security): the editor emits a formula
//     source string. The server remains the security boundary for
//     visibility decisions (FR-FORM-4 / FR-AUTH-7). The editor's
//     `ok: true` result is informational UX only.
//   - INV-5 (secrets by reference): the editor never asks for nor
//     renders secret material. The `context` prop is an evaluation
//     Env for variable resolution — never API keys, JWTs, or
//     credentials.
//   - INV-9 (no dangerouslySetInnerHTML): the formula source is the
//     `value` of a controlled `<textarea>` (which React renders as
//     a text node). The error message is rendered via
//     `<InlineFieldError>` (also a text node). No HTML construction
//     anywhere.
//   - FR-UI-NFR-2 (no client-side eval): this component makes ONE
//     `fetch` call to the server. It never imports expr-lang/expr,
//     expr-eval, wazero, fengari, wasmoon, `eval(`, or
//     `new Function(`. The existing apps/web/src/runtime/staticGuards.test.ts
//     AC-1c/AC-2b/AC-3e guard is structural.
//
// A11Y (AC-7):
//   - Programmatic label via `<label htmlFor>`.
//   - Inline error region is wired with `aria-describedby` + `id`.
//   - Full keyboard nav (Tab/Shift+Tab, Escape to cancel, Enter to
//     submit). The Validate button is a real `<button type="button">`.
//   - axe-core CI gate: zero serious/critical violations.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { InlineFieldError } from "@/design-system/error-states/InlineFieldError";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import {
  useFormulaValidate,
  type FormulaValidateResult,
  type UseFormulaValidateOptions,
} from "./useFormulaValidate";

/** Props contract — see AC-1. The widget is consumer-agnostic;
 *  visibility rules, record rules, and alert rules all share the
 *  same shape. The `fieldPath` is used for cache-key grouping + as
 *  a `data-field-path` attribute on the textarea (future-proofing
 *  for a per-rule error map; v1 renders the single `error` string). */
export interface FormulaEditorProps {
  /** Current formula source. Controlled — the parent owns it. */
  value: string;
  /** Only mutation channel. Fires per keystroke (≤16ms AC-2). */
  onChange: (next: string) => void;
  /** Identifier for the rule slot this editor fills. Used for
   *  cache-key grouping and as a `data-field-path` attribute on
   *  the textarea. NOT sent in the wire body (FR-VAL-4 contract
   *  is `{formula, context?}` per 02-impact-analysis.md). */
  fieldPath: string;
  /** Optional evaluation context (FR-VAL-4 wire contract). Opaque
   *  to the editor. Treated as non-sensitive (INV-5). */
  context?: Record<string, unknown>;
  /** Active locale for chrome strings. Optional — falls back to
   *  the i18n runtime's active locale when omitted. The locale
   *  is NOT sent in the wire body (see `fieldPath` note above). */
  locale?: "ru" | "kk" | "en";
  /** Disabled state. When true, the textarea + Validate button
   *  are read-only. */
  disabled?: boolean;
  /** API base override — defaults to "/api". Tests point MSW
   *  at a controlled origin via this prop. */
  apiBase?: string;
  /** Debounce window in ms — defaults to 300 per AC-3. Set to 0
   *  to disable debouncing (each call fires immediately). */
  debounceMs?: number;
  /** Optional label override for i18n. Defaults to
   *  `formula.editor.label` chrome. */
  labelKey?: string;
  /** Optional aria-describedby element id override. The default
   *  is `${fieldPath}-error` — match the FormRenderer's
   *  per-field-slot wiring convention. */
  errorId?: string;
  /** Optional imperative-validate handler override. Defaults to
   *  the hook's debounced validate. */
  onRequestValidate?: UseFormulaValidateOptions;
}

/** The single, reusable FormulaEditor component. */
export function FormulaEditor(props: FormulaEditorProps): React.ReactElement {
  const {
    value,
    onChange,
    fieldPath,
    context,
    locale,
    disabled = false,
    apiBase,
    debounceMs,
    labelKey = "formula.editor.label",
    errorId,
    onRequestValidate,
  } = props;

  const { t } = useTranslation();
  const effectiveErrorId = errorId ?? `${fieldPath}-error`;

  // Build hook options from the imperative override if supplied;
  // otherwise pass our own debounce + apiBase through.
  const hookOptions: UseFormulaValidateOptions = {
    ...(apiBase !== undefined ? { apiBase } : {}),
    ...(debounceMs !== undefined ? { debounceMs } : {}),
    ...(context !== undefined ? { context } : {}),
    ...(onRequestValidate ?? {}),
  };

  const {
    status,
    result,
    error: hookError,
    retryAfterSeconds,
    validate,
    cancel,
  } = useFormulaValidate(hookOptions);

  const onChangeHandler = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const next = e.target.value;
      onChange(next);
      // AC-3: schedule a debounced validate. The 300ms window
      // collapses a typing burst into a single fetch.
      validate(next);
    },
    [onChange, validate],
  );

  const onBlur = React.useCallback((): void => {
    // AC-3: validate fires immediately on blur (`immediate: true`).
    validate(value, { immediate: true });
  }, [validate, value]);

  const onClickValidate = React.useCallback((): void => {
    validate(value, { immediate: true });
  }, [validate, value]);

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      // Escape cancels any in-flight request (AC-4). It does NOT
      // mutate `value` — the user's source remains in the textarea.
      if (e.key === "Escape") {
        cancel();
      }
    },
    [cancel],
  );

  // ── Render: state-specific surface ────────────────────────────
  // AC-5: 6 UI states — loading, fetch-failure, permission-denied,
  // stale-version, 429-backpressure, successful-validated.

  // We render the textarea unconditionally + an error region
  // underneath that switches by status. This avoids re-mounting
  // the controlled input on every state transition (which would
  // drop focus / caret position mid-typing).
  const textarea = (
    <textarea
      id={`formula-editor-${fieldPath}`}
      data-testid={`formula-editor-${fieldPath}`}
      data-field-path={fieldPath}
      data-locale={locale}
      value={value}
      onChange={onChangeHandler}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      rows={4}
      spellCheck={false}
      autoComplete="off"
      aria-label={t(labelKey, "Formula")}
      aria-describedby={
        status === "success" && result?.ok === false
          ? effectiveErrorId
          : undefined
      }
      aria-invalid={
        status === "success" && result?.ok === false ? true : undefined
      }
      className="formula-editor-textarea"
    />
  );

  const validateButton = (
    <button
      type="button"
      data-testid={`formula-editor-validate-${fieldPath}`}
      onClick={onClickValidate}
      disabled={disabled || status === "loading"}
      className="formula-editor-validate"
      aria-label={t("formula.editor.validate", "Validate")}
    >
      {t("formula.editor.validate", "Validate")}
    </button>
  );

  // Per-state rendering of the error / status region beneath the
  // textarea. The textarea + button are always present; only this
  // region switches.
  const statusRegion = ((): React.ReactNode => {
    if (status === "loading") {
      return <SkeletonLayout />;
    }
    if (status === "permission-denied") {
      return <PermissionDenied />;
    }
    if (status === "stale-version") {
      return <StaleVersionError />;
    }
    if (status === "rate-limit") {
      return <RateLimitBackpressure retryAfterSeconds={retryAfterSeconds} />;
    }
    if (status === "fetch-failure") {
      return (
        <FetchError message={hookError?.message} onRetry={onClickValidate} />
      );
    }
    // status === "success" || "idle"
    if (status === "success" && result?.ok === false) {
      return (
        <InlineFieldError
          id={effectiveErrorId}
          message={result.error}
          {...(result.code !== undefined ? { code: result.code } : {})}
        />
      );
    }
    if (status === "success" && result?.ok === true) {
      // Informational chrome only — `ok: true` is not a security
      // boundary (INV-2). Show the inferred type for authoring
      // ergonomics.
      return (
        <p
          data-testid={`formula-editor-type-${fieldPath}`}
          className="formula-editor-type"
          aria-live="polite"
        >
          {t("formula.editor.type", "Type: {{type}}", {
            type: result.type,
          })}
        </p>
      );
    }
    return null;
  })();

  return (
    <div
      data-testid={`formula-editor-root-${fieldPath}`}
      className="formula-editor"
      data-status={status}
    >
      <label
        htmlFor={`formula-editor-${fieldPath}`}
        className="formula-editor-label"
      >
        {t(labelKey, "Formula")}
      </label>
      <div className="formula-editor-input-row">
        {textarea}
        {validateButton}
      </div>
      <div className="formula-editor-status-region">{statusRegion}</div>
    </div>
  );
}

/** Re-export the result type for consumers that want to type-narrow
 *  the imperative trigger result. */
export type { FormulaValidateResult };
